/**
 * PTEL Visor - MapLegend Component
 * Leyenda colapsable de tipos de infraestructura
 *
 * VIS-05 - Accesibilidad: WCAG 2.1 (aria-expanded, role, keyboard)
 */

import React, { memo, useState, useCallback } from 'react';
import type { InfrastructureType } from '../../types/infrastructure';
import { MARKER_CONFIGS, getTypesByCategory } from '../../services/tiles/MapMarkers';
import { MarkerIcon } from './MarkerIcon';
import { VIA_LABELS_LARGO } from '../../utils/procedenciaVia';

export interface MapLegendProps {
  /** Estado inicial (expandido/colapsado) */
  defaultExpanded?: boolean;
  /** Clase CSS adicional */
  className?: string;
}

const CATEGORY_LABELS: Record<string, string> = {
  emergency: 'Emergencias',
  services: 'Servicios',
  utilities: 'Suministros',
  infrastructure: 'Infraestructura',
};

export const MapLegend = memo<MapLegendProps>(function MapLegend({
  defaultExpanded = true,
  className = '',
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const categories = getTypesByCategory();

  const handleToggle = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  return (
    <div className={`map-legend ${className}`} data-testid="map-legend">
      <button
        type="button"
        onClick={handleToggle}
        aria-expanded={expanded}
        aria-controls="map-legend-content"
        className="map-legend__toggle"
        style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}
      >
        <span aria-hidden="true">{expanded ? '▼' : '▶'}</span>
        <span>Leyenda</span>
      </button>

      {expanded && (
        <ul
          id="map-legend-content"
          role="list"
          aria-label="Tipos de infraestructura"
          style={{ listStyle: 'none', padding: 0, margin: '8px 0 0' }}
        >
          {Object.entries(categories).map(([category, types]) => (
            <li key={category} style={{ marginBottom: '8px' }}>
              <strong style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                {CATEGORY_LABELS[category]}
              </strong>
              <ul role="list" style={{ listStyle: 'none', padding: 0, margin: '4px 0 0' }}>
                {types.map((type: InfrastructureType) => {
                  const config = MARKER_CONFIGS[type];
                  return (
                    <li
                      key={type}
                      style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '2px 0' }}
                    >
                      <span
                        style={{
                          width: '12px',
                          height: '12px',
                          borderRadius: '50%',
                          backgroundColor: config.color,
                          flexShrink: 0,
                        }}
                        aria-hidden="true"
                      />
                      <MarkerIcon type={type} size="sm" emojiOnly />
                      <span style={{ fontSize: '0.875rem' }}>{config.label}</span>
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
          {/* MAPLEY (DOP-1=a): el anillo discontinuo ámbar de MAPCEN, explicado.
              Estilo en línea equivalente a `.ptel-symbol-marker.ptel-marker-centroide`
              (infrastructure.css): el selector es compuesto y no aplica aquí. */}
          <li data-testid="map-legend-centroide" style={{ marginBottom: '8px' }}>
            <strong style={{ fontSize: '0.75rem', color: '#6b7280' }}>Procedencia</strong>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 2px 2px' }}>
              <span
                data-testid="map-legend-centroide-anillo"
                style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  backgroundColor: '#6b7280',
                  opacity: 0.55,
                  outline: '2px dashed #FBBF24',
                  outlineOffset: '1px',
                  flexShrink: 0,
                }}
                aria-hidden="true"
              />
              <span style={{ fontSize: '0.875rem' }}>{VIA_LABELS_LARGO.centroide}</span>
            </div>
          </li>
        </ul>
      )}
    </div>
  );
});

export default MapLegend;
